import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, Trash2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { isDemo } from '@/lib/demo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DATA_ITEMS = ['acc', 'listings', 'orders', 'chat', 'notifs', 'catalog'];

const SelfDelete = ({ user, getAuthHeader, logout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const needsPassword = user?.auth_provider !== 'google';
  const ready = confirm.trim().toUpperCase() === 'ELIMINAR' && (!needsPassword || password);

  const run = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/account/me`, { data: { password: password || null, confirm }, headers: { Authorization: getAuthHeader() } });
      toast.success(t('delAcc.done'));
      logout();
      navigate('/login', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || t('delAcc.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-red-200 rounded-lg p-5 shadow-sm space-y-4" data-testid="delete-account-self">
      <h2 className="text-base md:text-lg font-semibold text-slate-900 flex items-center gap-2"><Trash2 size={18} className="text-red-600" /> {t('delAcc.selfTitle', { name: user.business_name })}</h2>
      <p className="text-sm text-slate-600">{t('delAcc.selfDesc')}</p>
      {needsPassword && (
        <div>
          <Label htmlFor="del-password" className="text-sm">{t('delAcc.password')}</Label>
          <Input id="del-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="delete-account-password" className="mt-1" />
        </div>
      )}
      <div>
        <Label htmlFor="del-confirm" className="text-sm">{t('delAcc.typeWord')}</Label>
        <Input id="del-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="ELIMINAR" data-testid="delete-account-confirm" className="mt-1" autoComplete="off" />
      </div>
      <Button onClick={run} disabled={!ready || busy} data-testid="delete-account-submit" className="w-full bg-red-600 hover:bg-red-700 text-white">
        {busy ? t('delAcc.working') : t('delAcc.selfButton')}
      </Button>
    </div>
  );
};

const RequestForm = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/account/deletion-request`, { email, reason: reason || null });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || t('delAcc.error'));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 flex gap-3" data-testid="delete-request-sent">
        <CheckCircle2 className="text-emerald-600 shrink-0" />
        <div>
          <p className="font-semibold text-emerald-900">{t('delAcc.sentTitle')}</p>
          <p className="text-sm text-emerald-800 mt-1">{t('delAcc.sentDesc')}</p>
        </div>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm space-y-4" data-testid="delete-request-form">
      <h2 className="text-base md:text-lg font-semibold text-slate-900">{t('delAcc.formTitle')}</h2>
      <p className="text-sm text-slate-600">{t('delAcc.formDesc')}</p>
      <div>
        <Label htmlFor="del-email" className="text-sm">Email *</Label>
        <Input id="del-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="delete-request-email" className="mt-1" placeholder="tu@email.com" />
      </div>
      <div>
        <Label htmlFor="del-reason" className="text-sm">{t('delAcc.reason')}</Label>
        <Textarea id="del-reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="delete-request-reason" className="mt-1" rows={3} />
      </div>
      <Button type="submit" disabled={busy || !email} data-testid="delete-request-submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white">
        {busy ? t('delAcc.working') : t('delAcc.formButton')}
      </Button>
    </form>
  );
};

const DeleteAccountPage = () => {
  const { t } = useTranslation();
  const { user, getAuthHeader, logout } = useAuth();
  const loggedIn = !!user && !isDemo();
  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
      <div className="max-w-2xl mx-auto space-y-6" data-testid="delete-account-page">
        <Link to={loggedIn ? '/dashboard' : '/login'} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="delete-account-back"><ArrowLeft size={16} /> {t('delAcc.back')}</Link>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2"><ShieldAlert className="text-red-600" /> {t('delAcc.title')}</h1>
          <p className="text-slate-600 mt-2 text-sm sm:text-base">{t('delAcc.intro')}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-2">{t('delAcc.whatTitle')}</h2>
          <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700" data-testid="delete-account-data-list">
            {DATA_ITEMS.map((k) => <li key={k}>{t(`delAcc.data.${k}`)}</li>)}
          </ul>
          <p className="text-xs text-slate-500 mt-3">{t('delAcc.retention')}</p>
        </div>
        {loggedIn ? <SelfDelete user={user} getAuthHeader={getAuthHeader} logout={logout} /> : <RequestForm />}
        {!loggedIn && <p className="text-xs text-slate-500">{t('delAcc.loginHint')} <Link to="/login" className="text-emerald-700 underline">{t('delAcc.loginLink')}</Link></p>}
        <p className="text-xs text-slate-500">{t('delAcc.privacyHint')} <Link to="/privacidad" className="text-emerald-700 underline" data-testid="delete-account-privacy-link">{t('delAcc.privacyLink')}</Link></p>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
